import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { resolvePov } from '@/lib/battlePov';
import { modeBucket, type ModeBucket } from '@/lib/battleMode';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { setBattlesResultFilter, setBattlesModeFilter } from '@/store/core.slice';
import type { CRBattle } from '@/types/qbot';
import { EmptyState } from '@/components/common/EmptyState';
import { BattleRow } from './BattleRow';
import { BattlesFilters, type ResultFilter, type ModeFilter } from './BattlesFilters';

interface Props {
  battles: CRBattle[];
  playerTag: string;
  onRowClick: (battle: CRBattle, absIdx: number) => void;
}

export function BattlesListView({ battles, playerTag, onRowClick }: Props) {
  const { t } = useTranslation('cr');
  const dispatch = useAppDispatch();
  const result = useAppSelector((s) => s.core.battlesResultFilter) as ResultFilter;
  const storedMode = useAppSelector((s) => s.core.battlesModeFilter) as ModeFilter;
  const setResult = (r: ResultFilter) => dispatch(setBattlesResultFilter(r));
  const setModeF = (m: ModeFilter) => dispatch(setBattlesModeFilter(m));

  const availableModes = useMemo(
    () => [...new Set(battles.map((b) => modeBucket(b)))],
    [battles],
  );

  // Derive rather than trust: the stored bucket may not exist in the current
  // window (the mode rail hides itself when there is only one bucket, which
  // would strand the user on an unclearable empty list).
  const effectiveMode: ModeFilter =
    storedMode !== 'all' && availableModes.includes(storedMode as ModeBucket) ? (storedMode as ModeFilter) : 'all';

  // Keep the ORIGINAL window index so the detail route can index into page=1,
  // pageSize=25 exactly as before.
  const rows = useMemo(
    () =>
      battles
        .map((b, absIdx) => ({ b, absIdx, pov: resolvePov(b, playerTag) }))
        .filter(({ b, pov }) => {
          if (!pov) return false;
          if (result !== 'all' && pov.result !== result) return false;
          if (effectiveMode !== 'all' && modeBucket(b) !== effectiveMode) return false;
          return true;
        }),
    [battles, playerTag, result, effectiveMode],
  );

  return (
    <div className="flex flex-col gap-3">
      <BattlesFilters
        result={result}
        modeF={effectiveMode}
        onResult={setResult}
        onMode={setModeF}
        availableModes={availableModes}
      />

      {battles.length === 0 ? (
        <EmptyState>{t('battles.empty', { defaultValue: 'No recent battles to show.' })}</EmptyState>
      ) : rows.length === 0 ? (
        // Vuoto PER FILTRO, non per assenza: i filtri restano sopra, quindi
        // l'illustrazione qui non dice «non hai battaglie» ma «non con questi
        // filtri» — ed è il testo a fare quella distinzione, non il disegno.
        <EmptyState>
          {t('battles.emptyFiltered', { defaultValue: 'No battles match these filters.' })}
        </EmptyState>
      ) : (
        <ul className="flex flex-col gap-2">
          {rows.map(({ b, absIdx }) => (
            <li key={`${b.battleTime}-${absIdx}`}>
              <BattleRow battle={b} playerTag={playerTag} onClick={() => onRowClick(b, absIdx)} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
